'use client';

import type React from 'react';
import type { Link } from '@/app_components/common/Link';
import { getPageViewDataLayerObject, type TrackEventParams, trackEvent } from '@/app_lib/services/Gtm/utils';
import { cn } from '@/app_lib/utils/utils';

interface ActionableIconOwnProps<E extends 'button' | 'a' | typeof Link> {
  children: React.ReactNode;
  className?: string;
  as?: E;
  onClick?: (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>) => void;
  trackingData?: TrackEventParams;
}

type ActionableIconProps<E extends 'button' | 'a' | typeof Link> = ActionableIconOwnProps<E> &
  Omit<React.ComponentProps<E>, keyof ActionableIconOwnProps<E>>;

/**
 * Polymorphic Icon Button component.
 *
 * Use this when wrapping an icon that should be actionable (hover, focus, etc), such as a button or link.
 *
 * Renders default icon styling and padding with either:
 * - A native `<button/>` element by default
 * - A native `<a/>` element when used with `as="a"` (for external navigation)
 * - Our custom Link component (for internal navigation)
 *
 * This component will not accept any props not associated with it's given element type.
 */
export const ActionableIcon = <E extends 'button' | 'a' | typeof Link>({
  children,
  className,
  as,
  onClick,
  trackingData,
  ...rest
}: ActionableIconProps<E>) => {
  const Element = (as ?? 'button') as React.ElementType;

  return (
    <Element
      className={cn(
        'group/button flex min-h-11 min-w-11 shrink-0 items-center justify-center cursor-pointer md:min-h-fit md:min-w-fit',
        'hover:icon-primary-hover active:icon-primary-pressed disabled:cursor-not-allowed disabled:button-tertiary-disabled',
        className,
      )}
      onClick={(e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>) => {
        // TODO: check with data team to see if we can track failures
        if (trackingData) {
          const pageData = getPageViewDataLayerObject();
          trackEvent({ ...pageData, ...trackingData.data }, trackingData.eventType);
        }
        onClick?.(e);
      }}
      {...rest}
    >
      {children}
    </Element>
  );
};
